import type { ISSUE_TYPE } from '@app/shared/components/chat/chat.component';

export enum CONTENT_MIME_TYPE {
    TEXT = 'text/plain',
    MARKDOWN = 'text/markdown',
}

export enum FEEDBACK_TYPE {
    LIKE = 1,
    DISLIKE = -1,
    // biome-ignore lint/style/useLiteralEnumMembers: NULL is a valid value in this enum
    NONE = null,
}

export enum CHAT_ROLE {
    ASSISTANT = 'assistant',
    SYSTEM = 'system',
    USER = 'user',
    TOOL = 'tool',
    EVENT = 'event',
    INTERNAL = '_internal',
}

/** Status of a chat turn that is still running. A finished turn has no status. */
export enum TURN_STATUS {
    PENDING = 'PENDING', // Waiting for a free kernel
    RUNNING = 'RUNNING',
    CANCELLING = 'CANCELLING', // A cancel was requested and the owner worker has not ended the turn yet
}

/**
 * What the chat shows about the turn of the open conversation. Only the chat's owner sets it.
 * `idle` shows nothing, `failed` shows `error` (a send, a cancel or a reply load that failed), and the
 * other statuses show the turn status row. `error` may also come with them, for example while reading
 * the turn fails. `turn` is the last read of the turn's placeholder interaction, for the row's start
 * time and logs
 */
export interface ChatTurnState {
    status: 'idle' | 'pending' | 'running' | 'cancelling' | 'failed';
    error?: string;
    turn?: InteractionAdapter;
}

export const isTurnActive = ({ status }: ChatTurnState) =>
    status === 'pending' || status === 'running' || status === 'cancelling';

export interface ChatAttachments {
    id: number; // The attachment ID
    name: string; // The filename given by the user
    type: string; // The mimetype of the attachment
    contentType: 'image_file' | 'file';
    url: string; // The URL which can be used to download the file
}

// OpenAI Completions compatible content:
export type OpenAICompletionContent = (
    | { type: 'text'; text: string }
    | { type: 'image_file'; image_file: { file_id: number } }
    | { type: 'image_url'; image_url: { url: string } }
    | { type: 'file'; file_data: string; file_name: string }
    | { type: 'file'; file: { file_id: number } }
)[];

// NOTE: In InteractionContent, We should start supporting the remaining content types: Messages, Responses, etc.
export type InteractionContent = OpenAICompletionContent;

/** A small marker under a message, set by the owner of the chat (for example the issue a message is evidence of) */
export interface ChatInteractionTag {
    label: string;
    icon?: string;
    tooltip?: string;
    /** Whatever the owner needs to recognise the tag when it is clicked */
    data?: unknown;
}

export interface InteractionAdapter {
    id: number;
    name?: string;
    nameTooltip?: string;
    contentType?: CONTENT_MIME_TYPE;
    rating: FEEDBACK_TYPE;
    feedbackHistory?: {
        createdBy: string;
        createdDate: string;
        // Typically, either rating+message OR issueType+comment should be present. Not both.
        rating?: FEEDBACK_TYPE;
        message?: string; // A user friendly message for the `rating` change
        issueType?: ISSUE_TYPE;
        comment?: string;
    }[];
    timeTaken?: number;
    issueType?: string;
    likeCount?: number;
    dislikeCount?: number;
    comment?: string; // Temporary field until we have a better UX for commenting
    sortOrder?: number;
    context?: string;
    executionLogs?: string;
    createdBy?: string;
    createdDate?: string;
    lastModifiedBy?: string;
    lastModifiedDate?: string;
    errorMessage?: string;
    turnStatus?: TURN_STATUS | null; // Set while the turn that produces this interaction is running
    isAiGenerated?: boolean;
    debugTags?: string[];
    buttons?: { label: string; userMessage: string; tooltip?: string }[];

    // OpenAI message format:
    role?: CHAT_ROLE;
    content?: InteractionContent;
    tool_calls?: {
        id: string;
        function: {
            name: string;
            arguments: string;
        };
        type: string;
    }[];
    tool_call_id?: string;
}

export function getReviewerRating(
    interaction: InteractionAdapter,
    reviewer: string | null | undefined,
): FEEDBACK_TYPE {
    if (!reviewer) return FEEDBACK_TYPE.NONE;

    for (const feedback of [...(interaction.feedbackHistory ?? [])].reverse()) {
        // API comment entries also serialize rating as null. Imported AQ votes may omit message.
        if (
            feedback.createdBy === reviewer &&
            Object.hasOwn(feedback, 'rating') &&
            (feedback.rating != null ||
                feedback.message ||
                (!Object.hasOwn(feedback, 'comment') && !Object.hasOwn(feedback, 'issueType')))
        ) {
            return feedback.rating ?? FEEDBACK_TYPE.NONE;
        }
    }
    return FEEDBACK_TYPE.NONE;
}

/**
 * How many reviewers like and dislike a message now: the stored counts, or, for a message written before the counts
 * were stored, the latest vote of each reviewer in its feedback history.
 */
export function ratingCounts(interaction: InteractionAdapter): { likes: number; dislikes: number } {
    if (interaction.likeCount != null || interaction.dislikeCount != null) {
        return { likes: interaction.likeCount ?? 0, dislikes: interaction.dislikeCount ?? 0 };
    }
    const reviewers = new Set((interaction.feedbackHistory ?? []).map(entry => entry.createdBy));
    let likes = 0;
    let dislikes = 0;
    for (const reviewer of reviewers) {
        const rating = getReviewerRating(interaction, reviewer);
        if (rating === FEEDBACK_TYPE.LIKE) likes++;
        if (rating === FEEDBACK_TYPE.DISLIKE) dislikes++;
    }
    return { likes, dislikes };
}

export function updateReviewerRating(
    interaction: InteractionAdapter,
    reviewer: string,
    rating: FEEDBACK_TYPE,
    message: string,
): InteractionAdapter {
    const previousRating = getReviewerRating(interaction, reviewer);
    if (previousRating === rating) return interaction;
    const { likes, dislikes } = ratingCounts(interaction);
    return {
        ...interaction,
        rating,
        likeCount:
            likes +
            Number(rating === FEEDBACK_TYPE.LIKE) -
            Number(previousRating === FEEDBACK_TYPE.LIKE),
        dislikeCount:
            dislikes +
            Number(rating === FEEDBACK_TYPE.DISLIKE) -
            Number(previousRating === FEEDBACK_TYPE.DISLIKE),
        feedbackHistory: [
            ...(interaction.feedbackHistory ?? []),
            {
                createdBy: reviewer,
                createdDate: new Date().toISOString(),
                rating,
                message,
            },
        ],
    };
}
